import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus, Loader2, X, Upload, Search, SlidersHorizontal, Send, ChevronDown, ChevronUp, Users, CalendarOff, ShieldAlert } from 'lucide-react';
import { api } from '../api/client';
import { daysUntilCalendarDate, formatCalendarDate, parseCalendarDate, todayCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { useAuth } from '../contexts/AuthContext';
import { useTeamPermissions } from '../hooks/useTeamPermissions';
import { usePositions } from '../hooks/usePositions';
import { heldPositionIds } from '../types/position';
import SortTh from '../components/SortTh';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { getRoleLabel, getRoleAccessLabel, INVITABLE_ROLES, ALL_TENANT_ROLES } from '../lib/roles';
import { CERT_TYPE_LABEL_MAP as CERT_TYPE_LABELS } from '../lib/certTypes';
import { Tooltip } from '../components/ui/tooltip';

function getInitials(firstName?: string, lastName?: string) {
  return ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase() || '?';
}

function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Job title for a member: the free-text position title, else the position
 * resolved from positionId, else nothing. Never falls back to role — title and
 * role are separate concepts and a role must not masquerade as a title.
 */
function getTitle(member: any): string | null {
  return member.positionTitle || member.positionName || null;
}

function formatStartDate(dateStr?: string) {
  return formatCalendarDate(dateStr, { month: 'long', day: 'numeric', year: 'numeric' });
}

type CertWarning = { label: string; daysLeft: number; status: 'expired' | 'warning' };

function getCertWarnings(certs: any[], timeZone: string | null): CertWarning[] {
  const now = new Date();
  const warnings: CertWarning[] = [];

  for (const cert of certs) {
    if (!cert.expiryDate) continue;
    const daysLeft = daysUntilCalendarDate(cert.expiryDate, timeZone, now);
    if (daysLeft == null) continue;
    const certLabel = CERT_TYPE_LABELS[cert.certificationType] || CERT_TYPE_LABELS[cert.type] || cert.name || cert.certificationType || cert.type || 'Cert';

    if (daysLeft < 0) {
      warnings.push({ label: certLabel, daysLeft: 0, status: 'expired' });
    } else if (daysLeft <= 90) {
      warnings.push({ label: certLabel, daysLeft, status: 'warning' });
    }
  }

  return warnings;
}

/**
 * The register's status badge and row rail — dark ink on a saturated signal
 * fill, as on Quotes and Equipment. EXHAUSTIVE over `employment_status`
 * (active / inactive / terminated — the Postgres enum) plus `days_off`, which
 * is not a column: it is this screen's overlay for a member with approved time
 * off covering today, and it has always taken precedence over the employment
 * status in this cell.
 *
 * Four values, four of the five signal hues, so nothing has to share.
 *
 * Anything else — including a NULL status, which the column allows and which
 * GET /users?status=active does not count as active — reads `—` on slate with a
 * transparent rail. This cell used to fall back to "Active" in green for any
 * value it did not recognise, which is a confident claim about a row the screen
 * could not classify.
 */
type MemberStatus = 'active' | 'days_off' | 'inactive' | 'terminated';

const STATUS_BADGE: Record<MemberStatus, { label: string; fill: string; rail: string }> = {
  active:     { label: 'Active',     fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  days_off:   { label: 'Days Off',   fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue' },
  inactive:   { label: 'Inactive',   fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  terminated: { label: 'Terminated', fill: 'bg-fi-signal-red',   rail: 'border-l-fi-red' },
};

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

function statusSkin(member: any, isOnDaysOff: boolean) {
  if (isOnDaysOff) return STATUS_BADGE.days_off;
  const key = member.employmentStatus as MemberStatus | null | undefined;
  return (key && key !== 'days_off' && STATUS_BADGE[key]) || UNKNOWN_STATUS;
}

function MemberStatusBadge({ skin }: { skin: { label: string; fill: string } }) {
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * Invite state is the SECOND status on a row, so it takes the quiet outlined
 * pill rather than a second saturated badge competing with the first. The hue
 * rides on a dot, which is not text and needs no ink contrast.
 *
 * Exhaustive over the values `users.invite_status` is written with, plus
 * `expired`. An unrecognised value reads `—` with a slate dot; it used to be
 * shown as "Not Invited". The member with no email keeps its own label: that is
 * a fact about the row, not a guess.
 */
const INVITE_STATUS_STYLES: Record<string, { label: string; dot: string }> = {
  not_invited: { label: 'Not Invited', dot: 'bg-fi-signal-slate' },
  invited:     { label: 'Invited',     dot: 'bg-fi-signal-amber' },
  active:      { label: 'Active',      dot: 'bg-fi-signal-green' },
  expired:     { label: 'Expired',     dot: 'bg-fi-signal-red' },
};

const UNKNOWN_INVITE = { label: '—', dot: 'bg-fi-signal-slate' };

function getInviteDisplay(member: any): { label: string; dot: string } {
  if (!member.email) return { label: 'No email', dot: 'bg-fi-signal-slate' };
  return INVITE_STATUS_STYLES[member.inviteStatus] || UNKNOWN_INVITE;
}

function InvitePill({ display }: { display: { label: string; dot: string } }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text">
      <span aria-hidden="true" className={`size-1.5 rounded-full ${display.dot}`} />
      {display.label}
    </span>
  );
}

/**
 * A member's certification standing, in four states that must not collapse:
 *
 *   · alert   — an expired or due-within-90-days cert, driver's licence included
 *   · current — certs on file and none of them alerting
 *   · none    — the list arrived and holds no certification records. Nobody has
 *               checked anything, so this is NOT current: it used to read "All
 *               current", an absence rendering as a clearance on the column
 *               someone reads before putting a hand on a job.
 *   · unknown — the payload carried no certifications array at all. A dash.
 *
 * None and unknown are kept apart on purpose: GET /users always sends the
 * array, so "None on file" is a fact about the member while the dash is a fact
 * about the payload. The licence is a column on the user, not a certification
 * record, so a current licence does not make a member's certs "current" — an
 * expiring or expired one still raises the alert either way.
 *
 * One copy, read by the table, the cards, and the band's Certs figure, so the
 * figure counts exactly the rows in the alert state and no others.
 */
type CertState = 'alert' | 'current' | 'none' | 'unknown';

function getMemberCertInfo(member: any, timeZone: string | null): { state: CertState; warnings: CertWarning[] } {
  const certs = Array.isArray(member.certifications) ? member.certifications : null;
  // Days left are counted from the TENANT's today. With no zone there is no
  // today to count from, so a member with anything to count is UNKNOWN — never
  // "current" because every count came back null.
  if (!timeZone && ((certs && certs.length > 0) || member.driversLicenceExpiry)) {
    return { state: 'unknown', warnings: [] };
  }
  const warnings = certs ? getCertWarnings(certs, timeZone) : [];
  const dlDays = daysUntilCalendarDate(member.driversLicenceExpiry, timeZone);
  if (dlDays !== null) {
    if (dlDays < 0) warnings.push({ label: "Driver's Licence", daysLeft: 0, status: 'expired' });
    else if (dlDays <= 90) warnings.push({ label: "Driver's Licence", daysLeft: dlDays, status: 'warning' });
  }
  if (warnings.length > 0) return { state: 'alert', warnings };
  if (!certs) return { state: 'unknown', warnings };
  return { state: certs.length > 0 ? 'current' : 'none', warnings };
}

/** The two status chips. The query itself changes with the chip: GET /users
    filters server-side, so only the loaded chip's population is known. */
const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All' },
] as const;

const FILTER_SELECT =
  'appearance-none rounded-fi-control border bg-fieldiq-card pl-3 pr-8 py-1.5 text-sm focus:border-fi-amber/50 focus:outline-none cursor-pointer';
const SELECT_CHEVRON: React.CSSProperties = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
};

const sortButtonClass = (active: boolean) =>
  `flex items-center gap-1 rounded-fi-control px-3 py-1.5 text-xs font-bold transition-colors ${
    active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:text-white'
  }`;

function isRowSelectable(member: any): boolean {
  if (!member.email) return false;
  if (member.inviteStatus === 'active') return false;
  return true;
}

function getDisabledReason(member: any): string | null {
  if (!member.email) return 'No email on file — add an email to invite';
  if (member.inviteStatus === 'active') return 'Already active';
  return null;
}

type BulkInviteResult = { employee_id: string; name: string; email: string; outcome: string };

export default function TeamScreen() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const { company } = useAuth();
  // Per action, not per screen: add/import, invites, and status/delete are
  // three different API gates. See useTeamPermissions.
  const perms = useTeamPermissions();
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState('');
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('team-search') || '');
  const [filterPosition, setFilterPosition] = useState(() => sessionStorage.getItem('team-filterPos') || '');
  const [filterRole, setFilterRole] = useState(() => sessionStorage.getItem('team-filterRole') || '');
  const [sortBy, setSortBy] = useState<string>(() => sessionStorage.getItem('team-sortBy') || 'name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => (sessionStorage.getItem('team-sortDir') as 'asc' | 'desc') || 'asc');
  const [statusFilter, setStatusFilter] = useState<string>(() => sessionStorage.getItem('team-statusFilter') || 'active');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [inviteResults, setInviteResults] = useState<BulkInviteResult[] | null>(null);
  const [resultsExpanded, setResultsExpanded] = useState(false);

  // Persist filter/sort state so it survives navigation to employee profiles
  useEffect(() => {
    sessionStorage.setItem('team-search', searchQuery);
    sessionStorage.setItem('team-filterPos', filterPosition);
    sessionStorage.setItem('team-filterRole', filterRole);
    sessionStorage.setItem('team-sortBy', sortBy);
    sessionStorage.setItem('team-sortDir', sortDir);
    sessionStorage.setItem('team-statusFilter', statusFilter);
  }, [searchQuery, filterPosition, filterRole, sortBy, sortDir, statusFilter]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', { status: statusFilter }],
    queryFn: () => api.users.list({ status: statusFilter }).then(r => r.data.data ?? r.data ?? []),
  });

  const rawUsers: any[] = Array.isArray(data) ? data : [];

  /*
   * The position filter is the tenant's own positions, matched against each
   * member's HELD set (`positionIds`, API 0425 — primary and "can work as"),
   * so a hand whose secondary is Tong Hand appears under Tong Hand.
   *
   * It used to compare `m.specialty` against four hardcoded values. No
   * tenant's positions are spelled that way, so every choice matched nobody.
   *
   * A value restored from sessionStorage that is not one of this tenant's
   * active positions — an old specialty string, a deactivated position — is
   * ignored rather than applied: applying it would empty the list with the
   * select showing "All Positions".
   */
  const { data: positionOptions } = usePositions();
  const activeFilterPosition =
    filterPosition && positionOptions?.some((p) => p.id === filterPosition) ? filterPosition : '';

  // Fetch time-off data to show blue dot for members currently on days off
  const { data: timeOffData } = useQuery({
    queryKey: ['time-off-dashboard'],
    queryFn: () => api.reports.timeOffDashboard().then(r => r.data.data ?? r.data ?? {}),
  });
  const timeOff: any = timeOffData ?? {};
  const upcoming = timeOff.upcoming ?? timeOff.thisWeek ?? [];
  // The TENANT's today, to match the Postgres date columns. No zone, no today:
  // nobody is marked as currently off rather than guessing a day.
  const todayDate = todayCalendarDate(timeZone);
  const currentlyOffUserIds = new Set<string>(
    upcoming
      .filter((n: any) => todayDate && n.start_date && n.start_date <= todayDate && n.end_date >= todayDate)
      .map((n: any) => n.user_id)
      .filter(Boolean)
  );

  // Filter + sort
  const users = rawUsers
    .filter((m) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const name = `${m.firstName || ''} ${m.lastName || ''} ${m.email || ''}`.toLowerCase();
        if (!name.includes(q)) return false;
      }
      if (activeFilterPosition && !heldPositionIds(m)?.includes(activeFilterPosition)) return false;
      if (filterRole && m.role !== filterRole) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') {
        const aName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
        const bName = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        cmp = aName.localeCompare(bName);
      } else if (sortBy === 'start_date') {
        const aDate = parseCalendarDate(a.startDate)?.getTime() ?? 0;
        const bDate = parseCalendarDate(b.startDate)?.getTime() ?? 0;
        cmp = aDate - bDate;
      } else if (sortBy === 'position' || sortBy === 'title') {
        // 'zzz' sorts untitled members last in ascending order
        const aPos = getTitle(a) || 'zzz';
        const bPos = getTitle(b) || 'zzz';
        cmp = aPos.localeCompare(bPos);
      } else if (sortBy === 'phone') {
        cmp = (a.phoneNumber || '').localeCompare(b.phoneNumber || '');
      } else if (sortBy === 'status') {
        cmp = (a.employmentStatus || 'active').localeCompare(b.employmentStatus || 'active');
      } else if (sortBy === 'access') {
        cmp = getRoleAccessLabel(a.role).localeCompare(getRoleAccessLabel(b.role));
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const hasActiveFilters = !!searchQuery || !!activeFilterPosition || !!filterRole || statusFilter !== 'active';

  const selectableUsers = useMemo(() => users.filter(isRowSelectable), [users]);

  const allSelectableChecked = selectableUsers.length > 0 && selectableUsers.every(u => selectedIds.has(u.id));

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelectableChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableUsers.map(u => u.id)));
    }
  };

  const bulkInviteMutation = useMutation({
    mutationFn: (employeeIds: string[]) => api.users.bulkInvite(employeeIds),
    onSuccess: (response) => {
      const results: BulkInviteResult[] = response.data.results ?? response.data.data?.results ?? [];
      setInviteResults(results);
      setResultsExpanded(false);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => {
      showToast(err.response?.data?.error || 'Failed to send invites');
    },
  });

  const handleBulkInvite = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkInviteMutation.mutate(ids);
  };

  const handleSingleInvite = (memberId: string) => {
    bulkInviteMutation.mutate([memberId]);
  };

  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  /**
   * The band's figures and the chips' counts: tallies of the one list this
   * screen already loads, with no added query.
   *
   * WHY A TALLY IS HONEST HERE. GET /users returns every member matching its
   * `status` filter — no LIMIT, no pagination (users.routes.ts) — and no
   * endpoint returns per-status or per-invite counts, so the loaded rows ARE
   * the population. Every tally is over `rawUsers`, before search, position and
   * role narrow it, so a figure does not move when the operator types.
   *
   * BUT THE POPULATION IS THE CHIP'S. The status filter is sent to the API, so
   * with Active selected nobody inactive or terminated was loaded, and "All" has
   * no number the screen could honestly claim: it reads a dash, not the active
   * count. The Active chip is known on both chips — it is the whole list on its
   * own chip, and a tally of `employmentStatus === 'active'` on All, which is
   * the same comparison the API filters with.
   *
   * `undefined` until the list has arrived and when it was refused; the
   * `Array.isArray` above turns both into an empty array, and a tally of that
   * would be a confident zero. displayValue() renders undefined as a dash.
   *
   * DAYS OFF also needs the time-off dashboard, which is role-gated
   * (CREW_SCHEDULE_READ) where the list is not. A role it refuses sees a dash
   * there, never "0 off today".
   */
  const listLoaded = Array.isArray(data);
  const timeOffLoaded = timeOffData !== undefined;
  const tally = (pred: (m: any) => boolean): number | undefined =>
    listLoaded ? rawUsers.filter(pred).length : undefined;
  const chipCount = (value: string): number | undefined => {
    if (value === 'all') return statusFilter === 'all' ? tally(() => true) : undefined;
    return statusFilter === 'active' ? tally(() => true) : tally((m) => m.employmentStatus === 'active');
  };
  const invitedCount = tally((m) => !!m.email && m.inviteStatus === 'invited');
  const certAlertCount = tally((m) => getMemberCertInfo(m, timeZone).state === 'alert');
  const daysOffCount = timeOffLoaded ? tally((m) => currentlyOffUserIds.has(m.id)) : undefined;

  /**
   * The row opens its member on Enter or Space — but ONLY when the row itself
   * has focus. These rows hold a checkbox and a Send Invite / Resend button, so
   * this is the fe#647 exception (CLAUDE.md, copied from Rate Sheets): no
   * button role on the row, because that would make the controls inside it
   * presentational, and no handling of a key that bubbled up from one of them,
   * because Enter on Resend must resend rather than open the profile.
   */
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return; // a key on an inner control is that control's
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/team/${id}`);
    }
  };

  return (
    <div data-testid="team-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="People and access"
        title="Team"
        onBack={() => navigate('/')}
        backLabel="Back"
        actions={
          (perms.canImport || perms.canCreate) ? (
            <>
              {perms.canImport && (
                <button
                  type="button"
                  onClick={() => navigate('/import/employees')}
                  className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
                >
                  <Upload className="size-4" aria-hidden="true" />
                  Import
                </button>
              )}
              {perms.canCreate && (
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  className="fi-action fi-action--standard fi-action--primary"
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add
                </button>
              )}
            </>
          ) : undefined
        }
        stats={[
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'Members',
            sub: statusFilter === 'all' ? 'Every status' : 'Active only',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(tally(() => true)),
          },
          {
            icon: <Send className="size-4" aria-hidden="true" />,
            label: 'Invited',
            sub: 'Not yet signed in',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(invitedCount),
          },
          {
            // The third column, which the circle's edge crosses at 1024-1048px.
            // Keep this label no wider than Quotes' "Accepted" (about 70px).
            icon: <CalendarOff className="size-4" aria-hidden="true" />,
            label: 'Days Off',
            sub: 'Off today',
            tone: 'info',
            loading: isLoading,
            value: displayValue(daysOffCount),
          },
          {
            icon: <ShieldAlert className="size-4" aria-hidden="true" />,
            label: 'Certs',
            sub: 'Expired or due soon',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(certAlertCount),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The Locations pattern (#686): the list stays, and one line says who can change it. */}
          {(!perms.canCreate || !perms.canInvite) && (
            <p data-testid="team-readonly-note" className="text-xs text-fieldiq-muted">
              {!perms.canCreate && 'Only an admin can add or import team members.'}
              {!perms.canCreate && !perms.canInvite && ' '}
              {!perms.canInvite && 'Only an admin or office staff can send invites.'}
            </p>
          )}

          {/* Status chips. Counts are `chipCount` — see the tallies above for
              why All has no number while Active is selected. No number is
              claimed while the list is in flight. */}
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {STATUS_FILTERS.map((opt) => {
              const active = statusFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {opt.label}
                  {!isLoading && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {displayValue(chipCount(opt.value))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search, filters and sort */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 py-2.5 text-sm text-white placeholder-fieldiq-muted focus:border-fi-amber/50 focus:outline-none"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-muted hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SlidersHorizontal size={14} className="flex-shrink-0 text-fieldiq-muted" />

            <select
              value={activeFilterPosition}
              onChange={(e) => setFilterPosition(e.target.value)}
              aria-label="Filter by position"
              className={`${FILTER_SELECT} ${activeFilterPosition ? 'border-fi-amber text-white' : 'border-fieldiq-border text-fieldiq-text'}`}
              style={SELECT_CHEVRON}
            >
              <option value="">All Positions</option>
              {(positionOptions ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              aria-label="Filter by role"
              className={`${FILTER_SELECT} ${filterRole ? 'border-fi-amber text-white' : 'border-fieldiq-border text-fieldiq-text'}`}
              style={SELECT_CHEVRON}
            >
              <option value="">All Roles</option>
              {ALL_TENANT_ROLES.map(r => (
                <option key={r} value={r}>{getRoleLabel(r)}</option>
              ))}
            </select>

            <div className="flex-1" />

            <div className="flex items-center gap-1">
              <button onClick={() => toggleSort('name')} className={sortButtonClass(sortBy === 'name')}>
                Name {sortBy === 'name' && (sortDir === 'asc' ? 'A-Z' : 'Z-A')}
              </button>
              <button onClick={() => toggleSort('position')} className={sortButtonClass(sortBy === 'position')}>
                Position {sortBy === 'position' && (sortDir === 'asc' ? 'A-Z' : 'Z-A')}
              </button>
              <button onClick={() => toggleSort('start_date')} className={sortButtonClass(sortBy === 'start_date')}>
                Start Date {sortBy === 'start_date' && (sortDir === 'asc' ? '↑' : '↓')}
              </button>
            </div>

            {hasActiveFilters && (
              <button
                onClick={() => { setSearchQuery(''); setFilterPosition(''); setFilterRole(''); setStatusFilter('active'); }}
                className="rounded-fi-pill border border-fieldiq-border bg-fieldiq-card px-4 py-1.5 text-sm font-bold text-fieldiq-text transition-colors hover:text-white"
              >
                Clear
              </button>
            )}
          </div>

          {/* The register. The amber edge is on this panel; each row carries
              its status rail, never a second amber one. */}
          <section
            aria-labelledby="team-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Team register</p>
                <h2 id="team-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                  {statusFilter === 'all' ? 'All members' : 'Active members'}
                </h2>
              </div>
            </header>

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-fi-amber" />
              </div>
            )}

            {error && (
              <div className="px-4 py-12 text-center text-fi-red">Failed to load team members.</div>
            )}

            {!isLoading && !error && users.length === 0 && (
              <div className="px-4 py-12 text-center text-fieldiq-text">No team members found.</div>
            )}

            {!isLoading && !error && users.length > 0 && (
              <>
                {/* ── Desktop Table ── */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1180px] whitespace-nowrap text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      {/* Transparent 4px so the columns line up with the railed rows. */}
                      <tr className="border-l-4 border-l-transparent">
                        {perms.canInvite && (
                        <th className="w-[40px] px-4 py-3">
                          <input
                            type="checkbox"
                            aria-label="Select all invitable members"
                            checked={allSelectableChecked}
                            onChange={toggleSelectAll}
                            disabled={selectableUsers.length === 0}
                            className="h-4 w-4 cursor-pointer rounded border-fieldiq-border bg-fieldiq-dark text-fieldiq-accent focus:ring-fieldiq-accent focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                        )}
                        <th className="w-[50px] px-4 py-3 text-xs font-bold uppercase tracking-wider text-fieldiq-muted"></th>
                        <SortTh label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Title" field="title" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Phone" field="phone" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Start Date" field="start_date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Certs</th>
                        <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Invite</th>
                        <SortTh label="Access" field="access" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((member: any) => {
                        const isOnDaysOff = currentlyOffUserIds.has(member.id);
                        const skin = statusSkin(member, isOnDaysOff);
                        const initials = getInitials(member.firstName, member.lastName);
                        const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;
                        const startDate = formatStartDate(member.startDate);
                        const certInfo = getMemberCertInfo(member, timeZone);
                        const title = getTitle(member);
                        const accessLabel = getRoleAccessLabel(member.role);
                        const selectable = isRowSelectable(member);
                        const disabledReason = getDisabledReason(member);
                        const inviteDisplay = getInviteDisplay(member);
                        const inviteStatus = member.inviteStatus || 'not_invited';
                        const showInviteAction = perms.canInvite && selectable && inviteStatus !== 'active';

                        return (
                          <tr
                            key={member.id}
                            tabIndex={0}
                            onClick={() => navigate(`/team/${member.id}`)}
                            onKeyDown={rowKeys(member.id)}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skin.rail}`}
                          >
                            {perms.canInvite && (
                            <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                              <div className="group relative">
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${name}`}
                                  checked={selectedIds.has(member.id)}
                                  onChange={() => toggleSelect(member.id)}
                                  disabled={!selectable}
                                  className="h-4 w-4 cursor-pointer rounded border-fieldiq-border bg-fieldiq-dark text-fieldiq-accent focus:ring-fieldiq-accent focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40"
                                />
                                {disabledReason && (
                                  <div className="absolute left-6 top-1/2 z-20 hidden -translate-y-1/2 whitespace-nowrap rounded-lg border border-fieldiq-border bg-fieldiq-dark px-2.5 py-1.5 text-xs text-fieldiq-text shadow-lg group-hover:block">
                                    {disabledReason}
                                  </div>
                                )}
                              </div>
                            </td>
                            )}
                            <td className="px-4 py-2.5">
                              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-fieldiq-accent/15 text-xs font-bold text-fieldiq-accent">
                                {initials}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-sm font-extrabold text-white">{name}</td>
                            <td className="px-4 py-2.5">
                              {title ? (
                                <span className="inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-xs font-medium text-fieldiq-text">{title}</span>
                              ) : (
                                <span className="text-sm text-fieldiq-muted">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-fieldiq-text">{member.phoneNumber || '—'}</td>
                            <td className="px-4 py-2.5 text-sm text-fieldiq-text">{startDate || '—'}</td>
                            <td className="px-4 py-2.5">
                              {certInfo.state === 'alert' ? (
                                <span className={`text-sm font-medium ${certInfo.warnings[0].status === 'expired' ? 'text-red-400' : 'text-amber-400'}`}>
                                  {certInfo.warnings.filter(w => w.status === 'expired').length > 0
                                    ? `${certInfo.warnings.filter(w => w.status === 'expired').length} expired`
                                    : `${certInfo.warnings.length} expiring`}
                                </span>
                              ) : certInfo.state === 'current' ? (
                                <span className="text-sm font-medium text-green-400">All current</span>
                              ) : certInfo.state === 'none' ? (
                                <span className="text-sm text-fieldiq-muted">None on file</span>
                              ) : (
                                <span className="text-sm text-fieldiq-muted">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5">
                              <MemberStatusBadge skin={skin} />
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <InvitePill display={inviteDisplay} />
                                {showInviteAction && (
                                  <Tooltip content={inviteStatus === 'invited' ? 'Resend invite' : 'Send invite'}>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleSingleInvite(member.id); }}
                                      disabled={bulkInviteMutation.isPending}
                                      className="whitespace-nowrap text-xs font-bold text-fieldiq-accent hover:text-amber-300 disabled:opacity-50"
                                    >
                                      {inviteStatus === 'invited' ? 'Resend' : 'Send Invite'}
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-sm text-fieldiq-text">{accessLabel}</td>
                            <td className="py-2.5 pr-3">
                              <ChevronRight size={14} className="text-fieldiq-muted" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Mobile Card List — no rail here; that edge belongs to the
                    surrounding register, never to a card. ── */}
                <div className="space-y-3 p-4 md:hidden">
                  {users.map((member: any) => {
                    const isOnDaysOff = currentlyOffUserIds.has(member.id);
                    const skin = statusSkin(member, isOnDaysOff);
                    const initials = getInitials(member.firstName, member.lastName);
                    const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;
                    const certInfo = getMemberCertInfo(member, timeZone);
                    const title = getTitle(member);
                    const accessLabel = getRoleAccessLabel(member.role);
                    const inviteDisplay = getInviteDisplay(member);
                    const selectable = isRowSelectable(member);
                    const disabledReason = getDisabledReason(member);
                    const inviteStatus = member.inviteStatus || 'not_invited';
                    const showInviteAction = perms.canInvite && selectable && inviteStatus !== 'active';

                    return (
                      <div
                        key={member.id}
                        onClick={() => navigate(`/team/${member.id}`)}
                        className={`flex cursor-pointer items-start gap-3 rounded-fi-panel border bg-fieldiq-card p-4 transition-colors hover:border-fi-amber/40 ${selectedIds.has(member.id) ? 'border-fi-amber/50' : 'border-fieldiq-border'}`}
                      >
                        {perms.canInvite && (
                        <div className="flex shrink-0 flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <div className="group relative">
                            <input
                              type="checkbox"
                              aria-label={`Select ${name}`}
                              checked={selectedIds.has(member.id)}
                              onChange={() => toggleSelect(member.id)}
                              disabled={!selectable}
                              className="h-4 w-4 cursor-pointer rounded border-fieldiq-border bg-fieldiq-dark text-fieldiq-accent focus:ring-fieldiq-accent focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40"
                            />
                            {disabledReason && (
                              <div className="absolute left-6 top-1/2 z-20 hidden -translate-y-1/2 whitespace-nowrap rounded-lg border border-fieldiq-border bg-fieldiq-dark px-2.5 py-1.5 text-xs text-fieldiq-text shadow-lg group-hover:block">
                                {disabledReason}
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-fieldiq-accent/15 text-xs font-bold text-fieldiq-accent">
                          {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <p className="truncate text-sm font-extrabold text-white">{name}</p>
                            <MemberStatusBadge skin={skin} />
                          </div>
                          <p className="text-sm text-fieldiq-muted">{title ? `${title} · ${accessLabel}` : accessLabel}</p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <InvitePill display={inviteDisplay} />
                            {showInviteAction && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSingleInvite(member.id); }}
                                disabled={bulkInviteMutation.isPending}
                                className="text-xs font-bold text-fieldiq-accent hover:text-amber-300 disabled:opacity-50"
                              >
                                {inviteStatus === 'invited' ? 'Resend' : 'Send Invite'}
                              </button>
                            )}
                            {member.phoneNumber && <span className="text-sm text-fieldiq-muted">·</span>}
                            {member.phoneNumber && <span className="text-sm text-fieldiq-muted">{member.phoneNumber}</span>}
                            {certInfo.state === 'alert' ? (
                              <span className={`text-sm font-medium ${certInfo.warnings[0].status === 'expired' ? 'text-red-400' : 'text-amber-400'}`}>
                                {certInfo.warnings[0].status === 'expired' ? `${certInfo.warnings[0].label} expired` : `${certInfo.warnings[0].label} expiring`}
                              </span>
                            ) : certInfo.state === 'current' ? (
                              <span className="text-sm font-medium text-green-400">All certs current</span>
                            ) : certInfo.state === 'none' ? (
                              <span className="text-sm text-fieldiq-muted">No certs on file</span>
                            ) : null}
                          </div>
                        </div>
                        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-fieldiq-muted" />
                      </div>
                    );
                  })}
                </div>

                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  {users.length} of {rawUsers.length} shown
                </p>
              </>
            )}
          </section>

          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center justify-between rounded-fi-panel border border-fi-amber/40 bg-fieldiq-card px-4 py-3">
              <span className="text-sm text-fieldiq-text">
                {selectedIds.size} member{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleBulkInvite}
                disabled={bulkInviteMutation.isPending}
                className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
              >
                {bulkInviteMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {bulkInviteMutation.isPending ? 'Sending...' : `Send Invites (${selectedIds.size})`}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Invite Results Dialog */}
      {inviteResults && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border">
              <h3 className="text-lg font-bold text-white">Invite Results</h3>
              <button onClick={() => setInviteResults(null)} className="text-fieldiq-text hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {/* Summary counts */}
              <div className="flex flex-wrap gap-3">
                {(() => {
                  const invited = inviteResults.filter(r => r.outcome === 'invited').length;
                  const resent = inviteResults.filter(r => r.outcome === 'resent').length;
                  const skippedActive = inviteResults.filter(r => r.outcome === 'skipped_already_active').length;
                  const skippedNoEmail = inviteResults.filter(r => r.outcome === 'skipped_no_email').length;
                  const parts: string[] = [];
                  if (invited > 0) parts.push(`${invited} invited`);
                  if (resent > 0) parts.push(`${resent} resent`);
                  if (skippedActive > 0) parts.push(`${skippedActive} skipped (already active)`);
                  if (skippedNoEmail > 0) parts.push(`${skippedNoEmail} skipped (no email)`);
                  return (
                    <p className="text-sm text-white font-medium">
                      {parts.join(' · ')}
                    </p>
                  );
                })()}
              </div>

              {/* Expandable per-row breakdown */}
              <div>
                <button
                  onClick={() => setResultsExpanded(prev => !prev)}
                  className="flex items-center gap-1.5 text-sm text-fieldiq-accent hover:text-amber-300 font-medium"
                >
                  {resultsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {resultsExpanded ? 'Hide details' : 'Show per-person details'}
                </button>
                {resultsExpanded && (
                  <div className="mt-3 max-h-64 overflow-y-auto space-y-1.5">
                    {inviteResults.map((r, i) => {
                      const outcomeLabel =
                        r.outcome === 'invited' ? 'Invited' :
                        r.outcome === 'resent' ? 'Resent' :
                        r.outcome === 'skipped_already_active' ? 'Skipped — already active' :
                        r.outcome === 'skipped_no_email' ? 'Skipped — no email' :
                        r.outcome;
                      const outcomeColor =
                        r.outcome === 'invited' || r.outcome === 'resent' ? 'text-green-400' :
                        'text-fieldiq-muted';
                      return (
                        <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-fieldiq-dark/50">
                          <div className="min-w-0">
                            <span className="text-sm text-white font-medium">{r.name}</span>
                            {r.email && <span className="text-xs text-fieldiq-muted ml-2">{r.email}</span>}
                          </div>
                          <span className={`text-xs font-medium ${outcomeColor} shrink-0 ml-3`}>{outcomeLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setInviteResults(null)}
                  className="bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setShowAddModal(false);
            showToast('User created and invite sent');
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-[var(--fab-bottom)] left-1/2 -translate-x-1/2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-xl shadow-lg text-sm animate-fade-in z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function AddUserModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'field_worker',
    phoneNumber: '',
    positionId: '',
  });
  const [error, setError] = useState('');

  const { data: positions = [] } = usePositions();

  const mutation = useMutation({
    mutationFn: (data: typeof form) => api.users.create(data),
    onSuccess,
    onError: (err: any) => {
      // Surface the API's own message verbatim — a dispatcher creating a user
      // gets a 403 explaining which role it may not assign, which is far more
      // useful than a generic failure string. `error` is the repo-wide key;
      // `message` is accepted as a fallback for endpoints that use it.
      const data = err?.response?.data;
      const apiMessage =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        null;
      setError(apiMessage || 'Failed to create user.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.firstName || !form.lastName) {
      setError('Email, first name, and last name are required.');
      return;
    }
    setError('');
    const { positionId, ...rest } = form;
    mutation.mutate(positionId ? { ...rest, positionId } : rest as typeof form);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border">
          <h3 className="text-lg font-bold text-white">Add Team Member</h3>
          <button onClick={onClose} className="text-fieldiq-text hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-fieldiq-danger/20 border border-fieldiq-danger text-fieldiq-danger px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">First Name</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
              placeholder="name@company.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
            >
              {INVITABLE_ROLES.map(r => (
                <option key={r} value={r}>{getRoleLabel(r)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Position <span className="text-fieldiq-muted">(optional)</span></label>
            <select
              value={form.positionId}
              onChange={(e) => setForm({ ...form, positionId: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
            >
              <option value="">Select position...</option>
              {/* Already active-only and in the API's order (usePositions); an
                  in-place .sort() here would reorder the cached array. */}
              {positions
                .map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Phone Number</label>
            <input
              type="tel"
              value={form.phoneNumber}
              onChange={(e) => setForm({ ...form, phoneNumber: formatPhoneInput(e.target.value) })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:border-fieldiq-accent focus:outline-none"
              placeholder="(555) 123-4567"
              maxLength={14}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {mutation.isPending ? 'Creating...' : 'Add & Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
